import { throttle } from 'throttle-debounce';
import type {
  ICategoryCount,
  IListConf,
  IMovieData,
} from '../../project/types.d';

const { Const, Data, RenderMovie, Util } = window.Project;
const { CATEGORY, CATEGORY_TYPE, MOVIE } = Const;

const MIN = 20; // 最低動画表示件数。
const STEP = 20; // 無限スクロールの際の、動画数の増え幅。

// 動画リストを描画するクラス。
export class MovieListClass {
  el: HTMLElement; // コンテキスト
  movies: IMovieData[] = []; // 動画データ
  limit = MIN; // 動画リストの表示件数
  counts: ICategoryCount = { all: 0 }; // カテゴリごとの動画数
  shortLength = 0; // ショート動画の動画数

  constructor() {
    this.el = Util.one('.g-mediaList[data-list]')!;
    this.calcScrollPos = throttle(500, this.calcScrollPos.bind(this));
  }

  // HTMLのdata-list属性を設定オブジェクトとして扱う。
  get conf(): IListConf {
    return JSON.parse(this.el.dataset.list!);
  }

  // HTMLのdata-list属性を更新する。
  set conf(conf: IListConf) {
    if (conf.tags) {
      conf.tags = Util.uniq(conf.tags);
    }
    this.el.dataset.list = JSON.stringify(conf);
  }

  // 現在の動画データの、利用可能タグの一覧。
  get availableTags(): number[] {
    const ids = this.movies.reduce(
      (list: number[], a) => list.concat(a[MOVIE.TAGS]),
      []
    );
    return Util.uniq(ids);
  }

  // 実際に描画はせずに、動画数だけを取得する。
  async dryRun(conf: Partial<IListConf>): Promise<number> {
    const config = { ...this.conf, ...conf };
    const movies = await RenderMovie.getListData(config);
    return movies.length;
  }

  // 設定オブジェクトに基づいて、動画データを絞り込む。
  async update(conf: Partial<IListConf>): Promise<void> {
    this.conf = { ...this.conf, ...conf };
    let movies = await RenderMovie.getListData(this.conf, true);

    // すべて動画の場合は、ページの接頭辞に基づいてフィルタする。
    if (this.conf.page === Const.PAGE_PREFIX.MOVIE) {
      movies = movies.filter((a) => a[MOVIE.PAGE] === Const.PAGE_PREFIX.MOVIE);
    }
    await this.setCounts(movies);

    // カテゴリで絞り込む。
    if (this.conf.category === Const.CATEGORY_TYPE.SHORT) {
      movies = movies.filter((a) => a[MOVIE.PAGE] === Const.PAGE_PREFIX.SHORT);
    } else if (this.conf.category === Const.CATEGORY_TYPE.COLUMN) {
      movies = movies.filter((a) => a[MOVIE.PAGE] === Const.PAGE_PREFIX.COLUMN);
    } else if (this.conf.category && this.conf.category !== CATEGORY_TYPE.MOVIE) {
      movies = movies.filter((a) => a[MOVIE.CATEGORY] === this.conf.category);
    }

    this.shortLength = 0;
    this.movies = movies;
  }

  // 動画リストを書き出す。
  async render(): Promise<void> {
    const master = await Data.fetchMaster();
    if (this.conf.category === CATEGORY_TYPE.SHORT) {
      this.limit = this.calcLimit();
      const list = Util.times(this.limit, (i) => RenderMovie.getListShortItem(this.movies[i], master));
      this.el.innerHTML = list.join('');
      this.el.classList.toggle('g-mediaList-empty', list.length === 0);
      this.el.classList.add('g-mediaList-short');
    } else {
      const shorts = this.movies.filter((a) => a[MOVIE.PAGE] === Const.PAGE_PREFIX.SHORT);
      const movies = this.movies.filter((a) => a[MOVIE.PAGE] !== Const.PAGE_PREFIX.SHORT);

      this.shortLength = shorts.length;
      this.limit = this.calcLimit();

      const list = Util.times(this.limit, (i) => RenderMovie.getListItem(movies[i], master));

      if (shorts.length) {
        const short = RenderMovie.getCarouselSectionShort();
        if (shorts.length >= MIN) shorts.length = MIN;
        if (list.length >= 4) {
          list.splice(4, 0, short);
        } else {
          list.push(short);
        }
      }

      this.el.innerHTML = list.join('');
      this.el.classList.toggle('g-mediaList-empty', list.length === 0);
      this.el.classList.remove('g-mediaList-short');

      RenderMovie.renderCarouselShort('.g-shortMovieData [data-carousel]', shorts);
    }

    this.watchScroll(false);
    this.watchScroll(true);
  }

  // 引数の設定オブジェクトに基づいて、動画リストを更新する。
  async refresh(conf: Partial<IListConf>): Promise<void> {
    await this.update(conf);
    await this.render();
  }

  // カテゴリごとの動画数を更新する。
  async setCounts(movies: IMovieData[]) {
    const master = await Data.fetchMaster();
    const res: ICategoryCount = { all: movies.length };
    master.categories.forEach((a) => {
      const id = a[CATEGORY.ID];
      res[id] = movies.filter((b) => b[MOVIE.CATEGORY] === id).length;
    });
    // ショート動画の動画数を更新する
    res[CATEGORY_TYPE.SHORT] = movies.filter(
      (a) => a[MOVIE.PAGE] === 'sh'
    ).length;
    res[CATEGORY_TYPE.MOVIE] = movies.filter(
      (a) => a[MOVIE.PAGE] === 'mv'
    ).length;
    this.counts = res;
  }

  // スクロールが丁度いい位置に来たら、続きの動画リストを表示する。
  calcScrollPos() {
    if (window.innerHeight - 100 > this.el.getBoundingClientRect().bottom) {
      this.watchScroll(false);
      this.increaseLimit();
    }
  }

  // 無限スクロールのためにスクロールを監視する。
  watchScroll(on: boolean) {
    const method = on ? 'addEventListener' : 'removeEventListener';
    window[method]('scroll', this.calcScrollPos, false);
  }

  // 表示件数を取得する。
  calcLimit(): number {
    const length  = this.movies.length - this.shortLength;
    if (length < MIN) {
      return length;
    }
    if (this.limit < MIN) {
      return MIN;
    }
    return Math.min(this.limit, length);
  }

  // 動画表示件数をSTEP分増やす。
  increaseLimit() {
    if (this.limit < this.movies.length - this.shortLength) {
      this.limit += STEP;
      this.render();
    }
  }
}

export const MovieList = new MovieListClass();
